import React, { createContext, useContext, useState, useEffect } from 'react';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

const translations = {
  en: {
    nav: {
      home: 'Home',
      management: 'Management',
      marketplace: 'Marketplace',
      pricing: 'Pricing',
      downloads: 'Downloads',
      about: 'About',
      fintech: 'Fintech'
    },
    ui: {
      skip: 'Skip to content',
      menu: 'Menu',
      openMenu: 'Open menu',
      closeMenu: 'Close menu',
      close: 'Close',
      toLight: 'Switch to light theme',
      toDark: 'Switch to dark theme',
      switchLanguage: 'Passer en français',
      backToTop: 'Back to top',
      optional: 'Optional',
      loading: 'Loading'
    },
    home: {
      title: 'COSEBU: Connect Sellers and Buyers',
      subtitle: 'Your local market, Digitalized',
      kicker: 'Built in Burundi',
      standfirst: 'Software for the shops, wholesalers and buyers who make the Burundian economy work: run your stock and sales, reach nearby customers, and build the financial history that opens the door to credit.',
      openPlatform: 'Open the platform',
      seeHowItWorks: 'See how it works',
      demoLabel: 'Product tour',
      demoTitle: 'Two minutes inside COSEBU',
      demoCaption: 'A short walkthrough of the management app and the marketplace.',
      contextLabel: 'Why COSEBU exists',
      movementTitle: 'More than a startup — a movement to digitalise trade in Burundi',
      problemLabel: 'Today',
      directionLabel: 'Where we are going',
      statSellers: 'Businesses onboarded',
      statMarket: 'SMEs in Burundi',
      statExcluded: 'Financially excluded',
      problemsSellersTitle: 'Sellers: Current Challenges',
      problemsSellersText: 'Business management is chaotic. Most sellers track sales manually or not at all. There are no tools to track expenses, profits, or stock. Selling online is hard: foreign platforms like Amazon, Alibaba, and Jumia are not built for Burundi, payment gateways (PayPal, Stripe) don\'t support local banks or mobile money, shipping is expensive or unreliable, and promotion is difficult without simple ways to reach customers.',
      problemsBuyersTitle: 'Buyers: Current Challenges',
      problemsBuyersText: 'There is no trusted local marketplace with a wide selection. Comparing and discovering products is difficult, and people often have to visit physical stores—which is time-consuming.',
      mission: 'Mission: Build a trusted digital platform',
      missionDesc: 'For management and marketplace through a unified ecosystem in Burundi.',
      vision: 'Vision: Digitalize the future of business in Burundi',
      visionDesc: 'Build a strong local digital economy where sellers run efficiently and buyers easily find trusted products.',
      movementDesc: "COSEBU is on a mission to digitalize the future of business in Burundi. We aim to provide local entrepreneurs, small and mid-size enterprises with the tools they need to manage, grow, and connect their businesses efficiently. COSEBU is more than a startup—it's a movement to digitalize the future of trade in Burundi, with rules and systems tailored to Burundians."
    },
    management: {
      title: 'COSEBU Management',
      eyebrow: 'Run the shop',
      description: 'A business management tool designed for local shops and enterprises to handle inventory, customer records, daily sales tracking, subscriptions, and more—all from a phone or computer.',
      availableIn: 'Available in Kirundi',
      watchDemo: 'Watch Demo',
      tryProduct: 'Try Product',
      features: [
        'Inventory Management',
        'Customer Records',
        'Daily Sales Tracking',
        'Customer Management',
        'Mobile & Desktop Access'
      ]
    },
    marketplace: {
      title: 'COSEBU Marketplace',
      eyebrow: 'Reach buyers',
      description: 'A personalized marketplace experience that helps customers discover products from nearby shops with intelligent recommendations, WhatsApp support, and local delivery coordination based on the agreement of the sellers and buyers.',
      comingSoon: 'Coming Soon on PlayStore',
      download: 'Download',
      features: [
        'Discover Nearby Products',
        'AI Recommendations',
        'WhatsApp Support',
        'Local Delivery',
        'Personalized Experience'
      ]
    },
    downloads: {
      title: 'Downloads & Access',
      subtitle: 'Get COSEBU Marketplace AI (Android & iOS) or try COSEBU Management AI on the web.',
      eyebrow: 'Get started',
      forShoppers: 'For shoppers',
      forBusinesses: 'For businesses',
      webApp: 'Open web app',
      iosNote: 'iOS is in review. Join early access and we will send you a build.',
      earlyAccessCta: 'Join iOS early access',
      helpTitle: 'Need a hand getting set up?',
      helpText: 'Our team walks new businesses through their first week, in Kirundi, French or English.',
      marketplace: {
        title: 'COSEBU Marketplace AI',
        description: 'Discover and compare nearby products, chat with sellers on WhatsApp, and get local delivery coordination.',
        androidCta: 'Get Android App',
        iosCta: 'Try on iOS'
      },
      management: {
        title: 'COSEBU Management AI (Platform)',
        description: 'Run your business on the web with inventory, sales, customers, reports, and an AI assistant.',
        cta: 'Try Management Platform'
      },
      modal: {
        errorName: 'Please enter your full name.',
        errorWhatsapp: 'Enter a valid number, e.g. +25776935925.',
        errorEmail: 'Enter a valid email address.',
        privacy: 'We only use these details to send you access instructions.',
        title: 'Early Access Registration',
        apology: "We’re sorry—the Play Store release is delayed unexpectedly. Please register so we can add your email to early access. We’ll get back to you within 24–48 hours with instructions.",
        fullName: 'Full Name',
        fullNamePlaceholder: 'Enter your full name',
        whatsapp: 'WhatsApp Number',
        whatsappPlaceholder: 'e.g. +2576XXXXXXX',
        email: 'Email Address',
        emailPlaceholder: 'you@example.com',
        cancel: 'Cancel',
        submit: 'Submit',
        submitting: 'Submitting...',
        successTitle: 'Registration Received',
        successMessage: 'Thank you! You\'re on the early access list. We\'ll email you within 24–48 hours with instructions.',
        errorMessage: 'Something went wrong. Please try again in a moment.'
      }
    },
    pricing: {
      title: 'Pricing Plans',
      eyebrow: 'Plans',
      subtitle: 'Prices in Burundian francs. Cancel any time — no setup fee, no contract.',
      popular: 'Most popular',
      allFeatures: 'What you get',
      contactUs: 'Talk to us',
      freeTrialOffer: 'Limited Time: First users get free trial access to COSEBU Management AI for a month. Hurry up while the offer is still valid!',
      month: 'month',
      months: '6 months',
      year: 'year',
      readMore: 'Read More',
      readLess: 'Read Less',
      tryNow: 'Try Now',
      comingSoon: 'Coming Soon',
      free: 'Free',
      plans: {
        management: {
          name: 'COSEBU Management App',
          price: '40,000 FBU/month',
          features: [
            'Tracking inventory and product management',
            'Digital and sales management',
            'Management of customer (knowing the money you have in sales like debts or in cash)',
            'Tracking expenses',
            'Overview dashboard',
            'Management Financial reporting',
            '3 months of free access for users'
          ]
        },
        managementAI: {
          name: 'COSEBU Management AI',
          price: '60,000 FBU/month',
          features: [
            'All features from COSEBU Management App',
            'Financial reports and PDF generation of your business like the bilan financier, and so more',
            'Personal AI companion',
            'Supports shop owners and entrepreneurs with reports, analytics, reminders, and business tips tailored to their growth needs',
            '3 months of free access for users'
          ]
        },
        marketplaceUsers: {
          name: 'COSEBU Marketplace for Users',
          price: 'Free',
          features: [
            'Have access to see COSEBU management shops and their products',
            'Search for verified products with AI-powered price & value comparison',
            'Chat directly with seller on WhatsApp',
            'Make an order',
            'Have tailored product recommendations based on your interests'
          ]
        },
        marketplaceBrokers: {
          name: 'COSEBU Marketplace for Verified Brokers',
          price: 'Coming Soon',
          features: [
            'All features from COSEBU Marketplace for Users',
            'Brokers and micro-sellers can post directly in the marketplace their second hand product',
            'Verified access to a wider customer base'
          ]
        },
        customSolution: {
          name: 'Custom Solution',
          price: 'Tailored plan',
          features: [
            'We build what your business needs',
            'Custom workflows, dashboards, and integrations',
            'Priority support and delivery timeline'
          ]
        }
      }
    },
    fintech: {
      hero: {
        title: 'Cosebu Fintech — Making Burundian SMEs Bankable',
        paragraph1: 'In Burundi, over 45,000 small and medium businesses operate in the informal sector. They drive the economy every single day — but in the eyes of banks and financial institutions, they simply don\'t exist. No history. No data. No credit.',
        paragraph2: 'Cosebu is changing that.',
        paragraph3: 'Through Cosebu Management, every sale, every transaction, every expense is automatically recorded and structured. These data points build a reliable economic profile — a real financial history that banks and microfinance institutions can read, analyze and trust.',
        paragraph4: 'The result? Informal businesses that become visible, structured and bankable. For the first time, they can prove their activity and access the credit they deserve.',
        howItWorks: 'Cosebu Fintech works like this:',
        flow: 'Transactions → Data → Scoring → Credit → Growth',
        mission: 'Our Mission',
        missionText: 'Transform informal businesses into structured, visible and bankable enterprises — making financial inclusion a reality in Burundi.'
      },
      award: {
        badge: 'Winner',
        title: 'Award-Winning Innovation',
        competition: 'Shark Tank Burundi 2025',
        achievement: 'Best Tech Fintech Project in Burundi',
        prize: '$4,000',
        prizeLabel: 'Prize Awarded',
        description: 'Cosebu Fintech was recognized as the Best Tech Fintech Project in Burundi at the Shark Tank Burundi 2025 competition.'
      },
      journey: {
        title: 'Our Journey: 2 Phases',
        phase1Title: 'Phase 1 — Management & Marketplace',
        phase1Desc: 'We built and launched COSEBU Management and Marketplace — tools for local businesses to digitize operations, track sales, manage inventory, and connect with buyers.',
        phase1Status: 'Launched',
        phase2Title: 'Phase 2 — Fintech & Credit Scoring',
        phase2Desc: 'Using transaction data from Phase 1, we are building an ML-powered credit scoring system to make small businesses bankable and unlock access to credit.',
        phase2Status: 'Upcoming',
        businesses: '110',
        businessesLabel: 'Businesses Onboarded',
        volume: '$1,000+',
        volumeLabel: 'Transaction Volume Tracked',
        progressLabel: 'Progress to 1,000 businesses',
        progressValue: 11
      },
      problem: {
        title: 'The Problem We Solve',
        stat1Value: '45,000',
        stat1Label: 'SMEs in Burundi',
        stat1Desc: 'Small and medium enterprises operating across the country with limited digital tools.',
        stat2Value: '80%',
        stat2Label: 'Financially Excluded',
        stat2Desc: 'Of SMEs have no access to formal banking or credit services.',
        stat3Value: '15%',
        stat3Label: 'Bank Account Holders',
        stat3Desc: 'Only 15% of the population has a bank account.',
        stat4Value: '84%',
        stat4Label: 'No Financial History',
        stat4Desc: 'Of SMEs have no formal financial records, making them invisible to lenders.'
      },
      whatIs: {
        title: 'What is Cosebu Fintech?',
        subtitle: 'How It Works',
        step1Title: 'Data Collection',
        step1Desc: 'Transaction data is captured from COSEBU Management usage.',
        step2Title: 'ML Scoring',
        step2Desc: 'Machine learning models analyze patterns in business data.',
        step3Title: 'Bankability Assessment',
        step3Desc: 'Businesses receive a Cosebu Score reflecting creditworthiness.',
        step4Title: 'Credit Access',
        step4Desc: 'Scored businesses can access microloans from partner lenders.',
        scoringTitle: 'ML Scoring Criteria',
        criteria1: 'Growth',
        criteria1Desc: 'Revenue growth trends over time.',
        criteria2: 'Regularity',
        criteria2Desc: 'Consistency of sales and transactions.',
        criteria3: 'Seriousness',
        criteria3Desc: 'Business discipline and record-keeping quality.',
        criteria4: 'Conformity',
        criteria4Desc: 'Adherence to expected business patterns.',
        flowSummary: 'Transactions → Data → ML Scoring → Cosebu Score → Credit → Growth'
      },
      market: {
        title: 'Market Size',
        tamLabel: 'TAM',
        tamValue: '$5.4M',
        tamDesc: 'Total Addressable Market — all 45,000 SMEs in Burundi.',
        samLabel: 'SAM',
        samValue: '$810K',
        samDesc: 'Serviceable Addressable Market — 15% of TAM. Businesses already using digital tools that can consume our product.',
        somLabel: 'SOM',
        somValue: '$120K',
        somDesc: 'Serviceable Obtainable Market — 2.2% of TAM, 14.8% of SAM. Target for first phase (1,000 businesses).'
      },
      demo: {
        title: 'See Cosebu Fintech in Action'
      },
      cta: {
        title: 'Join the Mission',
        description: 'We are building the infrastructure for financial inclusion in Burundi. Partner with us to bring credit access to thousands of small businesses that have never been served by traditional finance.',
        button: 'Become a Partner'
      }
    },
    about: {
      title: 'About Us',
      eyebrow: 'Contact',
      emailLabel: 'Email',
      whatsappLabel: 'WhatsApp',
      responseTime: 'We usually reply within one business day.',
      description: 'COSEBU comes from Hyphen Tech as its parent house. For more details about COSEBU and Hyphen Tech, you can write to us at:',
      email: 'hyphentechinfo@gmail.com',
      whatsapp: '+25776935925',
      visitHyphenTech: 'Visit Hyphen Tech'
    },
    footer: {
      fromHyphenTech: 'From Hyphen Tech',
      address: 'Butanyerera, Burundi',
      email: 'support@cosebu.com',
      privacyPolicy: 'Privacy Policy',
      termsOfService: 'Terms of Service',
      navigation: 'Navigation',
      contact: 'Contact',
      legal: 'Legal',
      copyright: '© 2025 COSEBU. All rights reserved.',
      tagline: 'Connect Sellers and Buyers. Your local market, digitalized.'
    }
  },
  fr: {
    nav: {
      home: 'Accueil',
      management: 'Gestion',
      marketplace: 'Marché',
      pricing: 'Tarifs',
      downloads: 'Téléchargements',
      about: 'À propos',
      fintech: 'Fintech'
    },
    ui: {
      skip: 'Aller au contenu',
      menu: 'Menu',
      openMenu: 'Ouvrir le menu',
      closeMenu: 'Fermer le menu',
      close: 'Fermer',
      toLight: 'Passer au thème clair',
      toDark: 'Passer au thème sombre',
      switchLanguage: 'Switch to English',
      backToTop: 'Haut de page',
      optional: 'Facultatif',
      loading: 'Chargement'
    },
    home: {
      title: 'COSEBU: Connecter Vendeurs et Acheteurs',
      subtitle: 'Votre marché local, Numérisé',
      kicker: 'Conçu au Burundi',
      standfirst: "Des outils pour les boutiques, grossistes et acheteurs qui font tourner l'économie burundaise : gérez stock et ventes, touchez les clients proches, et construisez l'historique financier qui ouvre l'accès au crédit.",
      openPlatform: 'Ouvrir la plateforme',
      seeHowItWorks: 'Voir comment ça marche',
      demoLabel: 'Visite du produit',
      demoTitle: 'Deux minutes dans COSEBU',
      demoCaption: "Un aperçu rapide de l'app de gestion et du marché.",
      contextLabel: 'Pourquoi COSEBU existe',
      movementTitle: "Plus qu'une startup — un mouvement pour numériser le commerce au Burundi",
      problemLabel: "Aujourd'hui",
      directionLabel: 'Notre direction',
      statSellers: 'Entreprises intégrées',
      statMarket: 'PME au Burundi',
      statExcluded: 'Exclus financièrement',
      problemsSellersTitle: 'Vendeurs : Défis Actuels',
      problemsSellersText: 'La gestion est chaotique. La plupart suivent les ventes manuellement ou pas du tout. Pas d\'outils pour suivre dépenses, profits ou stock. Vendre en ligne est difficile : les plateformes étrangères (Amazon, Alibaba, Jumia) ne sont pas adaptées au Burundi, les paiements (PayPal, Stripe) ne prennent pas en charge les banques ou mobile money locaux, la livraison est coûteuse ou peu fiable, et la promotion est compliquée sans portée simple type WhatsApp.',
      problemsBuyersTitle: 'Acheteurs : Défis Actuels',
      problemsBuyersText: 'Pas de marché local fiable avec un large choix. Comparer et découvrir des produits est difficile, et il faut souvent aller en magasin—ce qui prend du temps.',
      mission: 'Mission : Construire une plateforme numérique de confiance',
      missionDesc: 'Pour la gestion et le marché via un écosystème unifié au Burundi.',
      vision: 'Vision : Numériser l\'avenir des affaires au Burundi',
      visionDesc: 'Construire une économie numérique locale forte où les vendeurs gèrent efficacement et les acheteurs trouvent facilement des produits fiables.',
      movementDesc: "COSEBU a pour mission de numériser l'avenir des affaires au Burundi. Nous visons à fournir aux entrepreneurs locaux et aux petites et moyennes entreprises les outils dont ils ont besoin pour gérer, développer et connecter leurs activités efficacement. COSEBU est plus qu'une startup : c'est un mouvement pour numériser l'avenir du commerce au Burundi, avec des règles et des systèmes adaptés aux Burundais."
    },
    management: {
      title: 'COSEBU Gestion',
      eyebrow: 'Gérer la boutique',
      description: 'Un outil de gestion d\'entreprise conçu pour les magasins et entreprises locales pour gérer l\'inventaire, les dossiers clients, le suivi des ventes quotidiennes, les abonnements, et plus encore—tout depuis un téléphone ou un ordinateur.',
      availableIn: 'Disponible en Kirundi',
      watchDemo: 'Voir la Démo',
      tryProduct: 'Essayer le Produit',
      features: [
        'Gestion d\'Inventaire',
        'Dossiers Clients',
        'Suivi des Ventes Quotidiennes',
        'Gestion des Clients',
        'Accès Mobile et Bureau'
      ]
    },
    marketplace: {
      title: 'COSEBU Marché',
      eyebrow: 'Atteindre les acheteurs',
      description: 'Une expérience de marché personnalisée qui aide les clients à découvrir des produits des magasins à proximité avec des recommandations intelligentes, le support WhatsApp, et la coordination de livraison locale basée sur l\'accord des vendeurs et acheteurs.',
      comingSoon: 'Bientôt Disponible sur PlayStore',
      download: 'Télécharger',
      features: [
        'Découvrir les Produits à Proximité',
        'Recommandations IA',
        'Support WhatsApp',
        'Livraison Locale',
        'Expérience Personnalisée'
      ]
    },
    downloads: {
      title: 'Téléchargements & Accès',
      subtitle: 'Obtenez COSEBU Marché IA (Android & iOS) ou essayez COSEBU Gestion IA sur le web.',
      eyebrow: 'Commencer',
      forShoppers: 'Pour les acheteurs',
      forBusinesses: 'Pour les entreprises',
      webApp: 'Ouvrir la version web',
      iosNote: "La version iOS est en revue. Inscrivez-vous à l'accès anticipé et nous vous enverrons une version.",
      earlyAccessCta: "Accès anticipé iOS",
      helpTitle: "Besoin d'aide pour démarrer ?",
      helpText: 'Notre équipe accompagne chaque nouvelle entreprise durant sa première semaine, en kirundi, français ou anglais.',
      marketplace: {
        title: 'COSEBU Marché IA',
        description: 'Découvrez et comparez des produits à proximité, discutez avec les vendeurs sur WhatsApp et bénéficiez d\'une coordination de livraison locale.',
        androidCta: 'Obtenir l\'app Android',
        iosCta: 'Essayer sur iOS'
      },
      management: {
        title: 'COSEBU Gestion IA (Plateforme)',
        description: 'Gérez votre entreprise sur le web: inventaire, ventes, clients, rapports et assistant IA.',
        cta: 'Essayer la Plateforme de Gestion'
      },
      modal: {
        errorName: 'Veuillez saisir votre nom complet.',
        errorWhatsapp: 'Saisissez un numéro valide, ex. +25776935925.',
        errorEmail: 'Saisissez une adresse email valide.',
        privacy: "Ces informations servent uniquement à vous envoyer les instructions d'accès.",
        title: 'Inscription Accès Anticipé',
        apology: "Nous sommes désolés — la sortie sur le Play Store est retardée de façon inattendue. Veuillez vous inscrire pour que nous ajoutions votre email à l'accès anticipé. Nous vous recontacterons sous 24–48h avec les instructions.",
        fullName: 'Nom Complet',
        fullNamePlaceholder: 'Entrez votre nom complet',
        whatsapp: 'Numéro WhatsApp',
        whatsappPlaceholder: 'ex. +2576XXXXXXX',
        email: 'Adresse Email',
        emailPlaceholder: 'vous@exemple.com',
        cancel: 'Annuler',
        submit: 'Envoyer',
        submitting: 'Envoi...',
        successTitle: 'Inscription Reçue',
        successMessage: 'Merci ! Vous êtes sur la liste d\'accès anticipé. Nous vous enverrons un email sous 24–48h avec les instructions.',
        errorMessage: 'Une erreur s\'est produite. Veuillez réessayer dans un instant.'
      }
    },
    pricing: {
      title: 'Plans Tarifaires',
      eyebrow: 'Offres',
      subtitle: 'Prix en francs burundais. Annulable à tout moment — sans frais de mise en service ni contrat.',
      popular: 'Le plus choisi',
      allFeatures: 'Ce que vous obtenez',
      contactUs: 'Nous contacter',
      freeTrialOffer: 'Offre Limitée: Les premiers utilisateurs obtiennent un accès d\'essai gratuit à COSEBU Management AI pendant un mois. Dépêchez-vous tant que l\'offre est encore valide!',
      month: 'mois',
      months: '6 mois',
      year: 'année',
      readMore: 'Lire Plus',
      readLess: 'Lire Moins',
      tryNow: 'Essayer Maintenant',
      comingSoon: 'Bientôt Disponible',
      free: 'Gratuit',
      plans: {
        management: {
          name: 'Application COSEBU Gestion',
          price: '40,000 FBU/mois',
          features: [
            'Suivi de l\'inventaire et gestion des produits',
            'Gestion numérique et des ventes',
            'Gestion des clients (connaître l\'argent que vous avez dans les ventes comme les dettes ou en espèces)',
            'Suivi des dépenses',
            'Tableau de bord général',
            'Rapports financiers de gestion',
            '3 mois d\'accès gratuit pour les utilisateurs'

          ]
        },
        managementAI: {
          name: 'COSEBU Gestion IA',
          price: '60,000 FBU/mois',
          features: [
            'Toutes les fonctionnalités de l\'Application COSEBU Gestion',
            'Rapports financiers et génération PDF de votre entreprise comme le bilan financier, et plus encore',
            'Compagnon IA personnel',
            'Soutient les propriétaires de magasins et entrepreneurs avec des rapports, analyses, rappels et conseils commerciaux adaptés à leurs besoins de croissance',
            '3 mois d\'accès gratuit pour les utilisateurs'
          ]
        },
        marketplaceUsers: {
          name: 'COSEBU Marché pour Utilisateurs',
          price: 'Gratuit',
          features: [
            'Accès pour voir les magasins de gestion COSEBU et leurs produits',
            'Recherche de produits vérifiés avec comparaison de prix et valeur alimentée par IA',
            'Chat direct avec le vendeur sur WhatsApp',
            'Passer une commande',
            'Recommandations de produits personnalisées basées sur vos intérêts'
          ]
        },
        marketplaceBrokers: {
          name: 'COSEBU Marché pour Courtiers Vérifiés',
          price: 'Bientôt Disponible',
          features: [
            'Toutes les fonctionnalités du COSEBU Marché pour Utilisateurs',
            'Les courtiers et micro-vendeurs peuvent publier directement sur le marché leurs produits d\'occasion',
            'Accès vérifié à une base de clients plus large'
          ]
        },
        customSolution: {
          name: 'Solution Personnalisée',
          price: 'Plan sur mesure',
          features: [
            'Nous construisons ce dont votre entreprise a besoin',
            'Workflows, tableaux de bord et intégrations personnalisés',
            'Support prioritaire et calendrier de livraison'
          ]
        }
      }
    },
    fintech: {
      hero: {
        title: 'Cosebu Fintech — Rendre les PME Burundaises Bancables',
        paragraph1: 'Au Burundi, plus de 45 000 petites et moyennes entreprises opèrent dans le secteur informel. Elles font tourner l\'économie chaque jour — mais aux yeux des banques et institutions financières, elles n\'existent tout simplement pas. Pas d\'historique. Pas de données. Pas de crédit.',
        paragraph2: 'Cosebu change la donne.',
        paragraph3: 'Grâce à Cosebu Management, chaque vente, chaque transaction, chaque dépense est automatiquement enregistrée et structurée. Ces données construisent un profil économique fiable — un véritable historique financier que les banques et institutions de microfinance peuvent lire, analyser et en qui elles peuvent avoir confiance.',
        paragraph4: 'Le résultat ? Des entreprises informelles qui deviennent visibles, structurées et bancables. Pour la première fois, elles peuvent prouver leur activité et accéder au crédit qu\'elles méritent.',
        howItWorks: 'Cosebu Fintech fonctionne ainsi :',
        flow: 'Transactions → Données → Scoring → Crédit → Croissance',
        mission: 'Notre Mission',
        missionText: 'Transformer les entreprises informelles en entreprises structurées, visibles et bancables — faire de l\'inclusion financière une réalité au Burundi.'
      },
      award: {
        badge: 'Lauréat',
        title: 'Une Innovation Récompensée',
        competition: 'Shark Tank Burundi 2025',
        achievement: 'Meilleur Projet Tech Fintech au Burundi',
        prize: '4 000 $',
        prizeLabel: 'Prix Remporté',
        description: 'Cosebu Fintech a été reconnu comme le Meilleur Projet Tech Fintech au Burundi lors de la compétition Shark Tank Burundi 2025.'
      },
      journey: {
        title: 'Notre Parcours : 2 Phases',
        phase1Title: 'Phase 1 — Gestion & Marché',
        phase1Desc: 'Nous avons construit et lancé COSEBU Gestion et Marché — des outils pour les entreprises locales afin de numériser leurs opérations, suivre les ventes, gérer l\'inventaire et se connecter avec les acheteurs.',
        phase1Status: 'Lancé',
        phase2Title: 'Phase 2 — Fintech & Scoring de Crédit',
        phase2Desc: 'En utilisant les données de transactions de la Phase 1, nous construisons un système de scoring de crédit alimenté par le ML pour rendre les petites entreprises bancables et débloquer l\'accès au crédit.',
        phase2Status: 'À venir',
        businesses: '110',
        businessesLabel: 'Entreprises Intégrées',
        volume: '$1 000+',
        volumeLabel: 'Volume de Transactions Suivi',
        progressLabel: 'Progression vers 1 000 entreprises',
        progressValue: 11
      },
      problem: {
        title: 'Le Problème que Nous Résolvons',
        stat1Value: '45 000',
        stat1Label: 'PME au Burundi',
        stat1Desc: 'Petites et moyennes entreprises opérant à travers le pays avec des outils numériques limités.',
        stat2Value: '80%',
        stat2Label: 'Exclus Financièrement',
        stat2Desc: 'Des PME n\'ont aucun accès aux services bancaires ou de crédit formels.',
        stat3Value: '15%',
        stat3Label: 'Titulaires de Comptes Bancaires',
        stat3Desc: 'Seuls 15% de la population possèdent un compte bancaire.',
        stat4Value: '84%',
        stat4Label: 'Sans Historique Financier',
        stat4Desc: 'Des PME n\'ont aucun dossier financier formel, les rendant invisibles aux prêteurs.'
      },
      whatIs: {
        title: 'Qu\'est-ce que Cosebu Fintech ?',
        subtitle: 'Comment Ça Marche',
        step1Title: 'Collecte de Données',
        step1Desc: 'Les données de transactions sont capturées depuis l\'utilisation de COSEBU Gestion.',
        step2Title: 'Scoring ML',
        step2Desc: 'Les modèles de machine learning analysent les schémas dans les données d\'entreprise.',
        step3Title: 'Évaluation de Bancabilité',
        step3Desc: 'Les entreprises reçoivent un Score Cosebu reflétant leur solvabilité.',
        step4Title: 'Accès au Crédit',
        step4Desc: 'Les entreprises évaluées peuvent accéder aux microcrédits des prêteurs partenaires.',
        scoringTitle: 'Critères de Scoring ML',
        criteria1: 'Croissance',
        criteria1Desc: 'Tendances de croissance des revenus au fil du temps.',
        criteria2: 'Régularité',
        criteria2Desc: 'Constance des ventes et des transactions.',
        criteria3: 'Sérieux',
        criteria3Desc: 'Discipline commerciale et qualité de tenue des registres.',
        criteria4: 'Conformité',
        criteria4Desc: 'Adhérence aux schémas commerciaux attendus.',
        flowSummary: 'Transactions → Données → Scoring ML → Score Cosebu → Crédit → Croissance'
      },
      market: {
        title: 'Taille du Marché',
        tamLabel: 'TAM',
        tamValue: '5,4 M$',
        tamDesc: 'Marché Total Adressable — les 45 000 PME au Burundi.',
        samLabel: 'SAM',
        samValue: '810 K$',
        samDesc: 'Marché Adressable et Serviceable — 15% du TAM. Entreprises utilisant déjà des outils numériques pouvant consommer notre produit.',
        somLabel: 'SOM',
        somValue: '120 K$',
        somDesc: 'Marché Obtenable et Serviceable — 2,2% du TAM, 14,8% du SAM. Objectif pour la première phase (1 000 entreprises).'
      },
      demo: {
        title: 'Voir Cosebu Fintech en Action'
      },
      cta: {
        title: 'Rejoignez la Mission',
        description: 'Nous construisons l\'infrastructure pour l\'inclusion financière au Burundi. Associez-vous à nous pour apporter l\'accès au crédit à des milliers de petites entreprises qui n\'ont jamais été servies par la finance traditionnelle.',
        button: 'Devenir Partenaire'
      }
    },
    about: {
      title: 'À Propos',
      eyebrow: 'Contact',
      emailLabel: 'Email',
      whatsappLabel: 'WhatsApp',
      responseTime: 'Nous répondons généralement sous un jour ouvré.',
      description: 'COSEBU vient de Hyphen Tech comme sa maison mère. Pour plus de détails sur COSEBU et Hyphen Tech, vous pouvez nous écrire à:',
      email: 'hyphentechinfo@gmail.com',
      whatsapp: '+25776935925',
      visitHyphenTech: 'Visiter Hyphen Tech'
    },
    footer: {
      fromHyphenTech: 'De Hyphen Tech',
      address: 'Butanyerera, Burundi',
      email: 'support@cosebu.com',
      privacyPolicy: 'Politique de Confidentialité',
      termsOfService: 'Conditions de Service',
      navigation: 'Navigation',
      contact: 'Contact',
      legal: 'Légal',
      copyright: '© 2025 COSEBU. Tous droits réservés.',
      tagline: 'Connecter Vendeurs et Acheteurs. Votre marché local, numérisé.'
    }
  }
};

const STORAGE_KEY = 'cosebu.language';

const detectLanguage = () => {
  if (typeof window === 'undefined') return 'en';
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === 'en' || stored === 'fr') return stored;
  } catch {
    /* storage unavailable */
  }
  return (navigator.language || 'en').toLowerCase().startsWith('fr') ? 'fr' : 'en';
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(detectLanguage);

  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  const toggleLanguage = () => {
    setLanguage((prev) => {
      const next = prev === 'en' ? 'fr' : 'en';
      try {
        window.localStorage.setItem(STORAGE_KEY, next);
      } catch {
        /* storage unavailable */
      }
      return next;
    });
  };

  const t = translations[language];

  return (
    <LanguageContext.Provider value={{ language, toggleLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};
